import { WISHLIST_ID, WishlistItem } from '@gate31/types/wishlist';
import Client from '@gate31/uikit/common/components/client/client';
import { Product } from '@gate31/types';
import { Gate31ApiV2 } from '@gate31/core/src/api/gate31-api';
import { debounce, DebouncedFunc } from 'lodash';
import FavoritesProducts from 'FavoritesProducts';
import ajaxAPI from 'ajaxAPI';
import Alpine from 'alpinejs';

export interface WishlistStore {
    wishlist: Map<number, WishlistItem>;
    userId?: number;
    isLoading: boolean;
    serializedWishlistCache?: string;
    readonly count: number;

    init: () => Promise<void>;
    initWishlist: (isAuth: boolean) => Promise<void>;
    sync: () => Promise<void>;
    add: (productId: number) => Promise<Product[]>;
    clear: () => Promise<unknown>;
    updateItem: (productId: number, hidden: boolean) => void;
    handleAdd: (productId: number) => void;
    handleRemove: (productId: number) => void;
    updateRemote?: DebouncedFunc<() => Promise<void>>;
    parseWishlist: (wishlistFieldValue: string) => WishlistItem[];
}

export default (): WishlistStore => ({
    wishlist: new Map<number, WishlistItem>(),
    serializedWishlistCache: Alpine.$persist('').as('wishlist-cache') as unknown as string,
    isLoading: true,
    get count() {
        return Array.from(this.wishlist.values()).filter(item => ! item.hidden).length;
    },

    async init() {
        this.updateRemote = debounce(async () => {
            if (! this.userId || ! this.wishlist.size) return;
            await Gate31ApiV2.updateRemoteWishlist(this.userId, Array.from(this.wishlist.values()));
        }, 500);

        try {
            await this.sync();
        } catch (e) {

        } finally {
            this.isLoading = false;
        }
    },

    async sync() {
        this.isLoading = true;

        const user = await Client.get();

        if (user.status !== 'ok') {
            return this.initWishlist(false);
        }
        this.userId = user.client.id;

        const serializedWishlist = user.client.fields_values.find(f => f.field_id === WISHLIST_ID)?.value;

        if (! serializedWishlist) {
            return this.initWishlist(true);
        }

        const parsed = this.parseWishlist(serializedWishlist);

        this.wishlist = new Map(parsed.map(item => [ Number(item.id), item ]));

        if (this.serializedWishlistCache === serializedWishlist) {
            return;
        }
        this.serializedWishlistCache = serializedWishlist;

        await this.clear();
        for (const item of parsed) {
            this.updateItem(Number(item.id), item.hidden);

            if (! item.hidden) {
                // eslint-disable-next-line
                this.add(+item.id)
            }
        }
        this.isLoading = false;
    },

    async initWishlist(isAuth: boolean) {
        const now = Date.now();

        const favorites: { products: Product[] } = await new Promise((resolve, reject) => {
            ajaxAPI.favorites.get().done(resolve).fail(reject);
        });

        if (! favorites) return;

        this.wishlist = new Map(
            favorites.products.map((p): [number, WishlistItem] =>
                [ p.id, {
                    hidden: false,
                    id: p.id.toString(),
                    syncedOn: now } ]
            ));
        if (isAuth) this.updateRemote!();
    },

    add(productId: number): Promise<Product[]> {
        return new Promise((resolve, reject) => {
            window.ajaxAPI.favorites
                .add(productId)
                .done((data: { products: Product[]; status: string }) => {
                    FavoritesProducts.update();
                    resolve(data.products);
                })
                .fail(reject);
        });
    },

    clear() {
        return new Promise((resolve, reject) => {
            window.ajaxAPI.favorites
                .clear()
                .done((data: {status: string}) => resolve(data.status))
                .fail(reject);
        });
    },

    updateItem(productId: number, hidden: boolean) {
        const now = Date.now();

        this.wishlist.set(productId, {
            hidden,
            id: productId.toString(),
            syncedOn: now
        });
    },

    handleAdd(productId: number) {
        this.updateItem(productId, false);
        this.updateRemote!();
    },

    handleRemove(productId: number) {
        this.updateItem(productId, true);
        this.updateRemote!();
    },

    parseWishlist(wishlistFieldValue: string) {
        const temp = document.createElement('p');

        temp.innerHTML = wishlistFieldValue;
        const result = JSON.parse(temp.textContent ?? '') as WishlistItem[];

        temp.remove();
        return result;
    }
});
